import { useEffect, useRef, useState } from "react";
import { api, API_BASE, getToken } from "@/lib/api";

/**
 * Hook that polls /api/bulk/jobs/{id} every `intervalMs` until the job reaches
 * a terminal state. Returns `{ job, done, error, refresh }`.
 */
const TERMINAL_STATUSES = ["completed", "completed_with_errors", "failed", "dry_run_completed"];

export function useBulkJob(jobId, { intervalMs = 2000, enabled = true } = {}) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const refresh = async () => {
    if (!jobId) return;
    try {
      const { data } = await api.get(`/bulk/jobs/${jobId}`);
      setJob(data);
      return data;
    } catch (e) {
      setError(e);
      return null;
    }
  };

  useEffect(() => {
    if (!enabled || !jobId) return;
    setError(null);
    setJob(null);
    let cancelled = false;
    const tick = async () => {
      const d = await refresh();
      if (cancelled) return;
      const terminal = d && TERMINAL_STATUSES.includes(d.status);
      if (!terminal) {
        timer.current = setTimeout(tick, intervalMs);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, enabled]);

  const done = job && TERMINAL_STATUSES.includes(job.status);
  return { job, done, error, refresh };
}

/** Build a fully-qualified URL for a bulk endpoint (used for anchor downloads). */
export function bulkUrl(path) {
  return `${API_BASE}${path}`;
}

/** Trigger a download with auth header (fetch → blob → anchor click). */
export async function authenticatedDownload(path, suggestedName) {
  const url = bulkUrl(path);
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = suggestedName || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
